import { useEffect, useRef, useState } from 'react'
import { Copy, Loader2 } from 'lucide-react'
import { Label } from '@/components/ui/label'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useCapacitySummary, useLayoutTemplate, useStorageRoots } from '@/hooks/useStorage'
import { levelsFromTemplate, type WizardLevel } from '@/lib/storageWizard'
import { CopyLayoutOptions } from './CopyLayoutOptions'

/**
 * Start a unit's layout from one that already exists.
 *
 * Copying is DESCRIBE + BUILD: the server describes the source as the same
 * levels the wizard produces, so the copy lands in the wizard as if somebody
 * had typed it — editable, and built by the one build path. Nothing is created
 * until they confirm, which is what makes a wrong pick free to undo.
 */

/** Units worth copying FROM — those that actually have a layout. */
export function useCopyableSources(excludeRootId: string) {
  const roots = useStorageRoots()
  const summary = useCapacitySummary()

  /*
   * Judged on `declared`, the same signal the units table calls "Not set up".
   * A grid on the ROOT is the wrong test: a unit whose first level is named
   * ("Vapor", "Liquid") declares no grid there and still has a full layout,
   * so that test would hide exactly the units worth cloning most.
   */
  const laidOut = new Set((summary.data ?? []).filter((r) => r.declared > 0).map((r) => r.nodeId))

  return {
    isLoading: roots.isLoading || summary.isLoading,
    sources: (roots.data ?? []).filter((r) => r.id !== excludeRootId && laidOut.has(r.id)),
  }
}

export function CopyLayoutPicker({
  excludeRootId,
  onSourceLevels,
}: {
  /** The unit being set up — never offered as its own source. */
  excludeRootId: string
  /** Called once per source pick, with the levels to prefill. The layout is all a copy brings. */
  onSourceLevels: (levels: WizardLevel[]) => void
}) {
  const { isLoading, sources } = useCopyableSources(excludeRootId)
  const [sourceId, setSourceId] = useState<string | null>(null)
  const template = useLayoutTemplate(sourceId)

  // Prefill once per source. Without this guard a refetch would throw away
  // every number the person has adjusted since picking.
  const prefilled = useRef<string | null>(null)
  useEffect(() => {
    const data = template.data
    if (!data || prefilled.current === data.sourceId) return
    prefilled.current = data.sourceId
    onSourceLevels(levelsFromTemplate(data.levels))
  }, [template.data, onSourceLevels])

  if (isLoading) return null
  if (sources.length === 0) return null

  const data = template.data

  return (
    <div className='space-y-3 rounded-lg border bg-muted/30 p-3'>
      <div className='space-y-1.5'>
        <Label className='flex items-center gap-1.5'>
          <Copy className='h-3.5 w-3.5' />
          Copy the layout of an existing unit
        </Label>
        <p className='text-[13px] text-muted-foreground'>
          Optional. It fills in the levels below, which you can still change before building.
        </p>
        <Select
          value={sourceId ?? 'none'}
          onValueChange={(v) => setSourceId(v === 'none' ? null : v)}
        >
          <SelectTrigger>
            <SelectValue placeholder='Start from scratch' />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value='none'>Start from scratch</SelectItem>
            {sources.map((r) => (
              <SelectItem key={r.id} value={r.id}>
                {r.name ?? r.code}
                {r.name && ` (${r.code})`}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </div>

      {template.isLoading && (
        <p className='flex items-center gap-2 text-[13px] text-muted-foreground'>
          <Loader2 className='h-3.5 w-3.5 animate-spin' />
          Reading that unit's layout…
        </p>
      )}

      {template.error && (
        <p className='text-[13px] text-destructive'>
          Could not read that unit's layout: {template.error.message}
        </p>
      )}

      {data && <CopyLayoutOptions template={data} />}
    </div>
  )
}
